import { RotateCcw } from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import DatePicker from '../../../../components/Forms/DatePicker/DatePicker';
import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../../components/Forms/Input/SelectField';
import SimpleSearchSelect from '../../../../components/Forms/Input/SimpleSearchSelect';

import {
  PurchaseStatusFilter,
  PurchaseSupplierOption,
} from '../types';

interface PurchaseFiltersProps {
  search: string;
  statusFilter: PurchaseStatusFilter;
  supplierId: number;
  dateFrom: string;
  dateTo: string;
  suppliers: PurchaseSupplierOption[];
  onSearchChange: (value: string) => void;
  onStatusChange: (value: PurchaseStatusFilter) => void;
  onSupplierChange: (supplierId: number) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onClearDates: () => void;
}

const PurchaseFilters = ({
  search,
  statusFilter,
  supplierId,
  dateFrom,
  dateTo,
  suppliers,
  onSearchChange,
  onStatusChange,
  onSupplierChange,
  onDateFromChange,
  onDateToChange,
  onClearDates,
}: PurchaseFiltersProps) => {
  const hasDateRange = Boolean(dateFrom || dateTo);

  const supplierOptions = [
    {
      id: 0,
      name: 'Todos los proveedores',
    },
    ...suppliers.map((supplier) => ({
      id: supplier.id,
      name: supplier.tradeName || supplier.name,
    })),
  ];

  return (
    <div className="border-b border-stroke p-5 dark:border-strokedark sm:px-7">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(240px,1.4fr)_minmax(170px,0.8fr)_minmax(220px,1fr)_minmax(160px,0.7fr)_minmax(160px,0.7fr)] xl:items-end">
        {/* =====================================================
            🔹 Buscar
        ===================================================== */}

        <div className="md:pb-5">
          <SearchInputField
            label="Buscar"
            name="purchaseSearch"
            value={search}
            iconKey="search"
            placeholder="Número de compra o proveedor..."
            withMargin={false}
            onChange={(event) =>
              onSearchChange(event.target.value)
            }
          />
        </div>

        {/* =====================================================
            🔹 Estado
        ===================================================== */}

        <SelectField
          label="Estado"
          name="purchaseStatus"
          value={statusFilter}
          iconKey="status"
          options={[
            {
              value: 'ALL',
              label: 'Todos los estados',
            },
            {
              value: 'DRAFT',
              label: 'Borrador',
            },
            {
              value: 'CONFIRMED',
              label: 'Confirmada',
            },
            {
              value: 'CANCELLED',
              label: 'Cancelada',
            },
          ]}
          onChange={(event) =>
            onStatusChange(
              event.target.value as PurchaseStatusFilter
            )
          }
        />

        {/* =====================================================
            🔹 Proveedor
        ===================================================== */}

        <SimpleSearchSelect
          label="Proveedor"
          name="purchaseSupplierFilter"
          selectedValue={supplierId}
          iconKey="supplier"
          placeholder="Seleccione un proveedor"
          searchPlaceholder="Buscar proveedor..."
          options={supplierOptions}
          onSelect={(option) =>
            onSupplierChange(option.id)
          }
        />

        {/* =====================================================
            🔹 Fecha desde
        ===================================================== */}

        <DatePicker
          label="Desde"
          name="purchaseDateFrom"
          value={dateFrom}
          iconKey="calendar"
          onChange={onDateFromChange}
        />

        {/* =====================================================
            🔹 Fecha hasta
        ===================================================== */}

        <DatePicker
          label="Hasta"
          name="purchaseDateTo"
          value={dateTo}
          iconKey="calendar"
          onChange={onDateToChange}
        />
      </div>

      {/* =====================================================
          🔹 Limpiar rango
      ===================================================== */}

      {hasDateRange && (
        <div className="mt-3 flex justify-end">
          <ActionButton
            label="Limpiar fechas"
            icon={<RotateCcw className="h-4 w-4" />}
            variant="secondary"
            size="sm"
            fullWidth={false}
            onClick={onClearDates}
          />
        </div>
      )}
    </div>
  );
};

export default PurchaseFilters;